import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Toast, ToastContainer, Spinner } from 'react-bootstrap';
import { Receipt, FileText, Save } from 'lucide-react';
import useAuthHeaders from '../hooks/useAuthHeaders';
import './Configuracion.css';

const API_URL = process.env.REACT_APP_API_URL;

const REGIMENES_FISCALES = [
  { code: '601', label: '601 - General de Ley Personas Morales' },
  { code: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { code: '605', label: '605 - Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', label: '606 - Arrendamiento' },
  { code: '607', label: '607 - Régimen de Enajenación o Adquisición de Bienes' },
  { code: '608', label: '608 - Demás ingresos' },
  { code: '610', label: '610 - Residentes en el Extranjero sin Establecimiento Permanente' },
  { code: '611', label: '611 - Ingresos por Dividendos (socios y accionistas)' },
  { code: '612', label: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '614', label: '614 - Ingresos por intereses' },
  { code: '615', label: '615 - Régimen de los ingresos por obtención de premios' },
  { code: '616', label: '616 - Sin obligaciones fiscales' },
  { code: '621', label: '621 - Incorporación Fiscal' },
  { code: '622', label: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { code: '623', label: '623 - Opcional para Grupos de Sociedades' },
  { code: '624', label: '624 - Coordinados' },
  { code: '625', label: '625 - Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { code: '626', label: '626 - Régimen Simplificado de Confianza' },
];

const RFC_REGEX = /^[A-ZÑ&]{3,4}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[A-Z\d]{3}$/;
const CP_REGEX = /^\d{5}$/;

const validate = (form) => {
  const errors = {};
  if (!form.razonSocial.trim()) {
    errors.razonSocial = 'El nombre o razón social es requerido.';
  }
  if (form.rfc) {
    const rfcUp = form.rfc.toUpperCase();
    if (rfcUp.length < 12 || rfcUp.length > 13) {
      errors.rfc = 'El RFC debe tener 12 o 13 caracteres.';
    } else if (!RFC_REGEX.test(rfcUp)) {
      errors.rfc = 'El formato del RFC no es válido.';
    }
  }
  if (form.codigoPostal && !CP_REGEX.test(form.codigoPostal)) {
    errors.codigoPostal = 'El Código Postal debe ser de 5 dígitos.';
  }
  return errors;
};

const DatosFiscales = () => {
  const { email, getToken } = useAuthHeaders();

  const [form, setForm] = useState({
    rfc: '',
    razonSocial: '',
    codigoPostal: '',
    regimenFiscal: '',
    emailFacturacion: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/Users/fiscal-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setForm({
            rfc: data.rfc ?? '',
            razonSocial: data.razonSocial ?? '',
            codigoPostal: data.codigoPostal ?? '',
            regimenFiscal: data.regimenFiscal ?? '',
            emailFacturacion: data.emailFacturacion || email || '',
          });
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };

    if (email !== undefined) {
      fetchData();
    }
  }, [email, getToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newVal = name === 'rfc' ? value.toUpperCase() : value;
    setForm(prev => ({ ...prev, [name]: newVal }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    const fieldErrors = validate(form);
    if (fieldErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/Users/fiscal-data`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rfc: form.rfc || null,
          razonSocial: form.razonSocial || null,
          codigoPostal: form.codigoPostal || null,
          regimenFiscal: form.regimenFiscal || null,
          emailFacturacion: form.emailFacturacion || null,
        }),
      });

      if (res.ok) {
        setToastMessage('Datos fiscales guardados correctamente.');
        setToastVariant('success');
      } else {
        setToastMessage('Error al guardar los datos fiscales.');
        setToastVariant('danger');
      }
    } catch {
      setToastMessage('Error de conexión. Intenta de nuevo.');
      setToastVariant('danger');
    } finally {
      setSaving(false);
      setShowToast(true);
    }
  };

  return (
    <div className="configuracion-container">
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide bg={toastVariant}>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Datos Fiscales</h2>
          <p className="page-subtitle text-muted">Información de facturación para tus comprobantes fiscales</p>
        </div>
      </div>

      <Row className="g-4">
        <Col lg={8}>
          <Card className="config-card border-0 shadow-sm">
            <Card.Header className="card-header-modern">
              <div className="d-flex align-items-center">
                <div className="header-icon-wrapper me-3">
                  <Receipt size={20} />
                </div>
                <h5 className="mb-0">Datos de Facturación</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span className="text-muted">Cargando...</span>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-modern">
                          <FileText size={16} className="me-2" />
                          RFC
                        </Form.Label>
                        <Form.Control
                          className="form-control-modern"
                          type="text"
                          name="rfc"
                          maxLength={13}
                          placeholder="XAXX010101000"
                          value={form.rfc}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={!!errors.rfc}
                        />
                        <Form.Control.Feedback type="invalid">{errors.rfc}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="form-label-modern">
                          <FileText size={16} className="me-2" />
                          Nombre / Razón Social <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          className="form-control-modern"
                          type="text"
                          name="razonSocial"
                          maxLength={300}
                          placeholder="Tu nombre o razón social"
                          value={form.razonSocial}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={!!errors.razonSocial}
                        />
                        <Form.Control.Feedback type="invalid">{errors.razonSocial}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="form-label-modern">
                          <FileText size={16} className="me-2" />
                          Código Postal
                        </Form.Label>
                        <Form.Control
                          className="form-control-modern"
                          type="text"
                          name="codigoPostal"
                          maxLength={5}
                          placeholder="00000"
                          value={form.codigoPostal}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={!!errors.codigoPostal}
                        />
                        <Form.Control.Feedback type="invalid">{errors.codigoPostal}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={8}>
                      <Form.Group>
                        <Form.Label className="form-label-modern">
                          <FileText size={16} className="me-2" />
                          Régimen Fiscal
                        </Form.Label>
                        <Form.Select
                          className="form-control-modern"
                          name="regimenFiscal"
                          value={form.regimenFiscal}
                          onChange={handleChange}
                        >
                          <option value="">Selecciona tu régimen fiscal</option>
                          {REGIMENES_FISCALES.map(r => (
                            <option key={r.code} value={r.code}>{r.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-modern">
                          <FileText size={16} className="me-2" />
                          Correo de contacto para facturas
                        </Form.Label>
                        <Form.Control
                          className="form-control-modern"
                          type="email"
                          name="emailFacturacion"
                          maxLength={255}
                          placeholder="correo@ejemplo.com"
                          value={form.emailFacturacion}
                          onChange={handleChange}
                        />
                        <Form.Text className="text-muted">
                          A este correo se enviarán tus facturas electrónicas.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Button className="btn-save mt-4" type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save size={18} className="me-2" />
                        Guardar Datos Fiscales
                      </>
                    )}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DatosFiscales;
